import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Button, Input } from "reactstrap";

const DataTable = ({
    data = [],
    columns,
    keyField = "id",
    emptyMessage = "Nenhum registro encontrado",
    emptySubMessage = "",
    isLoading = false,
    noCard = false
}) => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Reset pagination when data length or rowsPerPage changes
    useEffect(() => {
        setPage(1);
    }, [data.length, rowsPerPage]);

    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;

    // Ensure the current page is within valid range
    const currentPage = Math.min(Math.max(1, page), totalPages);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        return data.slice(startIndex, startIndex + rowsPerPage);
    }, [data, currentPage, rowsPerPage]);

    const canGoBack = currentPage > 1;
    const canGoForward = currentPage < totalPages;

    return (
        <React.Fragment>
            {/* Top Toolbar: Rows per page selector */}
            {data.length > 0 && (
                <div className={`px-4 py-3 ${noCard ? "bg-transparent" : "bg-white border-bottom"} d-flex justify-content-between align-items-center flex-wrap gap-2`}>
                    <div className="d-flex align-items-center mb-0 text-muted font-size-13">
                        <span className="me-2 fw-medium text-uppercase">Mostrar</span>
                        <Input
                            type="select"
                            bsSize="sm"
                            className="form-select border-light-subtle shadow-none rounded-3 cursor-pointer py-1 w-auto fw-bold"
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Input>
                        <span className="ms-2 fw-medium text-uppercase">registros por página</span>
                    </div>
                </div>
            )}

            {/* Table Core */}
            <div className={`table-responsive ${noCard ? "rounded-0 shadow-sm bg-white" : ""}`}>
                <Table className="align-middle table-nowrap mb-0" hover transparent={noCard ? 1 : 0}>
                    <thead className="table-light">
                        <tr>
                            {columns.map((col, idx) => (
                                <th key={idx} className={`font-size-13 fw-bold ${col.headerClassName || ""}`} style={col.style}>
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => (
                                <tr key={row[keyField] || rowIndex}>
                                    {columns.map((col, idx) => (
                                        <td key={idx} className={col.className} style={col.cellStyle}>
                                            {col.render ? col.render(row, rowIndex, { page: currentPage, rowsPerPage }) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-5">
                                    <div className="text-muted">
                                        <h6 className="fw-bold text-dark mt-3">{emptyMessage}</h6>
                                        {emptySubMessage && <p className="mb-0 small">{emptySubMessage}</p>}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Bottom Toolbar: Pagination Control */}
            {data.length > 0 && (
                <div className={`px-4 py-3 ${noCard ? "bg-transparent" : "bg-white border-top"} d-flex justify-content-between align-items-center flex-wrap gap-3`}>
                    <div className="text-muted small fw-medium">
                        Mostrando {(currentPage - 1) * rowsPerPage + 1} a {Math.min(currentPage * rowsPerPage, totalRows)} de {totalRows}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                        <Button
                            color="light"
                            size="sm"
                            className="border-0 shadow-sm rounded-0 px-3 py-1 fw-bold d-flex align-items-center"
                            disabled={!canGoBack}
                            onClick={() => setPage(page - 1)}
                        >
                            <i className="mdi mdi-chevron-left font-size-18 me-1"></i>
                            Anterior
                        </Button>
                        <div className="mx-2 d-flex align-items-center gap-1">
                            {/* Simple page numbers */}
                            {[...Array(totalPages)].map((_, i) => (
                                <Button
                                    key={i}
                                    color={currentPage === i + 1 ? "primary" : "white"}
                                    size="sm"
                                    className={`border-0 rounded-0 shadow-none fw-bold p-1 px-2 ${currentPage !== i + 1 ? 'text-muted' : ''}`}
                                    onClick={() => setPage(i + 1)}
                                >
                                    {i + 1}
                                </Button>
                            )).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))}
                        </div>
                        <Button
                            color="light"
                            size="sm"
                            className="border-0 shadow-sm rounded-0 px-3 py-1 fw-bold d-flex align-items-center"
                            disabled={!canGoForward}
                            onClick={() => setPage(page + 1)}
                        >
                            Próxima
                            <i className="mdi mdi-chevron-right ms-1 font-size-18"></i>
                        </Button>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

DataTable.propTypes = {
    data: PropTypes.array,
    columns: PropTypes.array.isRequired,
    keyField: PropTypes.string,
    emptyMessage: PropTypes.string,
    emptySubMessage: PropTypes.string,
    isLoading: PropTypes.bool
};


export default DataTable;
